/**
 * IITFoundationLogin
 * -------------------------------------------------------------------------
 * IIT Foundation — student landing page with an embedded login panel.
 *
 * Route: /iit-foundation/login
 *
 *   - Hero + glass login card, trust strip
 *   - Marquee, Why choose, Subjects, Study notes carousel, Learning path,
 *     Results, Testimonials, FAQ, closing CTA, footer
 *   - Real `foundationLogin` mutation via react-apollo — on success it sets
 *     the foundation cookies and redirects to /student/home
 *   - "Register" opens a free-trial modal driving the real three-step
 *     send OTP -> verify OTP -> register flow, then lands the new student on
 *     /iit-foundation/student-dashboard
 *
 * Styling lives in ./iitfoundationlogin.scss, scoped under .iitf-page.
 * -------------------------------------------------------------------------
 */

import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { graphql } from "react-apollo";
import gql from "graphql-tag";

import { setFoundationStudent } from "../foundationSession";
/* Two Rizee lockups: navy for the white topbar, white for the navy-950 footer. */
import CommonHeader from "../../components/navbars/CommonHeader";
import BrandLogo from "../../components/navbars/BrandLogo";
import rizeeLogoWhite from "../../../images/registrations/logo-white.png";
import heroStudents1 from "../../../images/IIT_foundation2.png";
import heroStudents2 from "../../../images/IIT_childrens1.png";
import heroStudents3 from "../../../images/IIT_foundation3.png";

/* The page renders outside FoundationLayout, so it pulls in the foundation
   design tokens itself and carries .fdn-theme on its root. */
import "../theme/_fdn-theme.scss";
import "./iitfoundationlogin.scss";

/* Mirrors maskEmail in the back end's foundationCrypto.js:
   rahul.sharma@gmail.com -> 'ra****@gmail.com'. */
function maskEmail(email) {
  const n = String(email || "").trim().toLowerCase();
  const at = n.lastIndexOf("@");
  if (at < 1) return n ? "****" : "";
  const local = n.slice(0, at);
  const keep = local.length <= 2 ? 1 : 2;
  return `${local.slice(0, keep)}****${n.slice(at)}`;
}

const LOGIN_USER = gql`
  mutation($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      refreshToken
      user {
        username
        userlevel
        email
        valid
        name
        institution_id
        institute_name
      }
    }
  }
`;

/* Classes come from f_class so the dropdown submits real grade_id values
   rather than labels the backend would have to string-match.

   FALLBACK_GRADES keeps the dropdown usable if that query has not resolved
   (e.g. a backend not yet serving getFoundationGrades). The ids mirror the
   deterministic f_class seed in db-mysql/foundation_schema.sql (1=Class 6 …
   5=Class 10); the server list overrides this whenever it loads. */
const FALLBACK_GRADES = [
  { id: "1", label: "Class 6" },
  { id: "2", label: "Class 7" },
  { id: "3", label: "Class 8" },
  { id: "4", label: "Class 9" },
  { id: "5", label: "Class 10" },
];

const FOUNDATION_GRADES = gql`
  query {
    getFoundationGrades {
      id
      label
      class_num
    }
  }
`;

/* Free-trial registration is a three-step server flow:
   send OTP -> verify OTP -> register. The backend refuses to write a
   registration whose mobile has not been verified, so the OTP step is
   not skippable from here. */
const FOUNDATION_LOGIN = gql`
  mutation($username: String!, $password: String!) {
    foundationLogin(username: $username, password: $password) {
      status
      message
      token
      refreshToken
      user {
        id
        name
        grade_id
        class
        class_num
        school
        mobile_mask
        email_mask
      }
    }
  }
`;

const SEND_FOUNDATION_OTP = gql`
  mutation($params: FoundationOtpInput!) {
    sendFoundationOtp(params: $params) {
      status
      message
      target_mask
      expires_in
      resend_after
    }
  }
`;

const VERIFY_FOUNDATION_OTP = gql`
  mutation($params: FoundationVerifyOtpInput!) {
    verifyFoundationOtp(params: $params) {
      status
      message
      verified
      attempts_left
    }
  }
`;

const REGISTER_FOUNDATION_STUDENT = gql`
  mutation($params: FoundationRegistrationInput!) {
    registerFoundationStudent(params: $params) {
      status
      message
      id
    }
  }
`;

/* ------------------------------------------------------------------ */
/* Static content                                                      */
/* ------------------------------------------------------------------ */

const HERO_FEATURES = [
  {
    tone: "c1",
    title: "Concept Clarity",
    desc: "NCERT to Advanced level coverage",
    path: (
      <>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
      </>
    ),
  },
  {
    tone: "c2",
    title: "Daily Practice",
    desc: "Topic-wise tests & assignments",
    path: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
        <path d="M14 2v6h6" />
        <path d="m9 15 2 2 4-4" />
      </>
    ),
  },
  {
    tone: "c3",
    title: "AI Performance",
    desc: "Smart analytics & personalised reports",
    path: (
      <>
        <path d="M12 2v3" />
        <rect x="4" y="5" width="16" height="13" rx="3" />
        <circle cx="9" cy="11.5" r="1.3" />
        <circle cx="15" cy="11.5" r="1.3" />
        <path d="M2 11h2" />
        <path d="M20 11h2" />
        <path d="M9 21h6" />
      </>
    ),
  },
  {
    tone: "c4",
    title: "Mock Tests",
    desc: "Regular exam-pattern practice tests",
    path: (
      <>
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M6 4h12v5a6 6 0 0 1-12 0V4Z" />
        <path d="M9 21h6" />
        <path d="M12 15v6" />
      </>
    ),
  },
];

const TRUST = [
  {
    tone: "a1",
    title: "AI-Guided Plan",
    desc: "3–5 daily tasks, auto-scheduled",
    path: (
      <>
        <path d="M22 10 12 5 2 10l10 5 10-5Z" />
        <path d="M6 12.5V17c0 1.1 2.7 2.5 6 2.5s6-1.4 6-2.5v-4.5" />
      </>
    ),
  },
  {
    tone: "a2",
    title: "Structured Learning",
    desc: "Step by step roadmap",
    path: (
      <>
        <path d="M12 3v18" />
        <path d="M5 7h4" />
        <path d="M15 12h4" />
        <path d="M5 17h4" />
        <circle cx="12" cy="7" r="1.6" />
        <circle cx="12" cy="12" r="1.6" />
        <circle cx="12" cy="17" r="1.6" />
      </>
    ),
  },
  {
    tone: "a3",
    title: "Regular Assessments",
    desc: "Track & improve your score",
    path: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
        <path d="M14 2v6h6" />
        <path d="m9 15 2 2 4-4" />
      </>
    ),
  },
  {
    tone: "a4",
    title: "Smart Revision",
    desc: "Weak topics auto-queued",
    path: (
      <>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
        <path d="M9.5 9a2.5 2.5 0 1 1 3.4 2.3c-.6.3-.9.8-.9 1.4" />
        <path d="M12 15h.01" />
      </>
    ),
  },
];

const WHY = [
  {
    ac: "var(--fdn-blue)",
    acbg: "var(--fdn-blue-soft)",
    acbd: "var(--fdn-blue)",
    title: "AI-generated notes",
    desc: "Every chapter comes with clear notes, worked examples, formulae and quick summaries to learn and revise from.",
    path: (
      <>
        <path d="M12 2 2 7l10 5 10-5-10-5Z" />
        <path d="M5 11.5V17c0 1.4 3.1 3 7 3s7-1.6 7-3v-5.5" />
      </>
    ),
  },
  {
    ac: "var(--fdn-green)",
    acbg: "var(--fdn-green-soft)",
    acbd: "var(--fdn-green)",
    title: "NCERT to Advanced",
    desc: "School syllabus is covered end to end first, then extended into olympiad and JEE-level problems, one concept at a time.",
    path: (
      <>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
        <path d="M9 7h7" />
        <path d="M9 11h5" />
      </>
    ),
  },
  {
    ac: "var(--fdn-violet)",
    acbg: "var(--fdn-violet-soft)",
    acbd: "var(--fdn-violet)",
    title: "AI-powered practice",
    desc: "Tests adapt to what a student keeps getting wrong, so revision time goes to weak chapters instead of the ones already mastered.",
    path: (
      <>
        <path d="M12 2v3" />
        <rect x="4" y="5" width="16" height="13" rx="3" />
        <circle cx="9" cy="11.5" r="1.4" />
        <circle cx="15" cy="11.5" r="1.4" />
        <path d="M2 11h2" />
        <path d="M20 11h2" />
        <path d="M9 21h6" />
      </>
    ),
  },
  {
    ac: "var(--fdn-orange)",
    acbg: "var(--fdn-orange-soft)",
    acbd: "var(--fdn-orange)",
    title: "Parent visibility",
    desc: "Weekly reports on consistency, test trends and chapter-level accuracy — no guessing how things are going.",
    path: (
      <>
        <path d="M3 3v18h18" />
        <path d="m7 15 4-5 3 3 5-6" />
      </>
    ),
  },
  {
    ac: "var(--fdn-teal)",
    acbg: "var(--fdn-teal-soft)",
    acbd: "var(--fdn-teal)",
    title: "Smart revision",
    desc: "Weak topics queue themselves into short 10–15 minute revision packs, timed to when you'd forget them.",
    path: (
      <>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
        <path d="M9.5 9a2.5 2.5 0 1 1 3.4 2.3c-.6.3-.9.8-.9 1.4" />
        <path d="M12 15h.01" />
      </>
    ),
  },
  {
    ac: "var(--fdn-pink)",
    acbg: "var(--fdn-pink-soft)",
    acbd: "var(--fdn-pink)",
    title: "Olympiad ready",
    desc: "NTSE, NSO and IMO-style question sets sit alongside the core syllabus, building real exam temperament early.",
    path: (
      <>
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
        <path d="M6 4h12v5a6 6 0 0 1-12 0V4Z" />
        <path d="M9 21h6" />
        <path d="M12 15v6" />
      </>
    ),
  },
];

const SUBJECTS = [
  {
    ac: "var(--fdn-violet)",
    acbg: "var(--fdn-violet-soft)",
    acbd: "var(--fdn-violet)",
    off: 18,
    title: "Physics",
    pct: "94% syllabus ahead of plan",
    desc: "From measurement and motion to optics, built on derivations rather than formula lists.",
    tags: ["Mechanics", "Optics", "Electricity"],
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=300&q=80",
  },
  {
    ac: "var(--fdn-teal)",
    acbg: "var(--fdn-teal-soft)",
    acbd: "var(--fdn-teal)",
    off: 30,
    title: "Chemistry",
    pct: "90% syllabus ahead of plan",
    desc: "Atomic structure, bonding and reactions explained with clear visuals before the equations.",
    tags: ["Physical", "Organic", "Inorganic"],
    img: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=300&q=80",
  },
  {
    ac: "var(--fdn-orange)",
    acbg: "var(--fdn-orange-soft)",
    acbd: "var(--fdn-orange)",
    off: 12,
    title: "Mathematics",
    pct: "96% syllabus ahead of plan",
    desc: "Algebra to coordinate geometry, with a daily problem set that builds real speed.",
    tags: ["Algebra", "Geometry", "Trigonometry"],
    img: "https://images.unsplash.com/photo-1509228468518-180dd4864904?auto=format&fit=crop&w=300&q=80",
  },
  {
    ac: "var(--fdn-green)",
    acbg: "var(--fdn-green-soft)",
    acbd: "var(--fdn-green)",
    off: 35,
    title: "Biology",
    pct: "88% syllabus ahead of plan",
    desc: "Cells, life processes and genetics explained with diagrams students can redraw from memory.",
    tags: ["Cell Biology", "Genetics", "Ecology"],
    img: "https://images.unsplash.com/photo-1530026186672-2cd00ffc50fe?auto=format&fit=crop&w=300&q=80",
  },
];

const NOTES = [
  {
    id: "n1", cls: "6", subj: "sci", subjLabel: "Science",
    title: "Food: Where Does It Come From?",
    img: "https://images.unsplash.com/photo-1490818387583-1baba5e638af?auto=format&fit=crop&w=500&q=80",
    alt: "Food and nutrition notes",
    pages: "14 pages", questions: "22 questions", note: "Updated Jun 2026",
  },
  {
    id: "n2", cls: "6", subj: "math", subjLabel: "Maths",
    title: "Knowing Our Numbers",
    img: "https://images.unsplash.com/photo-1509228468518-180dd4864904?auto=format&fit=crop&w=500&q=80",
    alt: "Number system notes",
    pages: "18 pages", questions: "35 questions", note: "Updated May 2026",
  },
  {
    id: "n3", cls: "7", subj: "sci", subjLabel: "Science",
    title: "Heat and Temperature",
    img: "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?auto=format&fit=crop&w=500&q=80",
    alt: "Heat and temperature notes",
    pages: "16 pages", questions: "28 questions", note: "Updated Jun 2026",
  },
  {
    id: "n4", cls: "7", subj: "math", subjLabel: "Maths",
    title: "Integers & Rational Numbers",
    img: "https://images.unsplash.com/photo-1596495577886-d920f1fb7238?auto=format&fit=crop&w=500&q=80",
    alt: "Integers notes",
    pages: "20 pages", questions: "40 questions", note: "Updated Apr 2026",
  },
  {
    id: "n5", cls: "8", subj: "phy", subjLabel: "Physics",
    title: "Force, Friction and Pressure",
    img: "https://images.unsplash.com/photo-1517976487492-5750f3195933?auto=format&fit=crop&w=500&q=80",
    alt: "Force and pressure notes",
    pages: "22 pages", questions: "38 questions", note: "NTSE tagged",
  },
  {
    id: "n6", cls: "8", subj: "chem", subjLabel: "Chemistry",
    title: "Synthetic Fibres & Metals",
    img: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=500&q=80",
    alt: "Materials chemistry notes",
    pages: "19 pages", questions: "30 questions", note: "Updated May 2026",
  },
  {
    id: "n7", cls: "9", subj: "phy", subjLabel: "Physics",
    title: "Motion, Force and Gravitation",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=500&q=80",
    alt: "Motion and gravitation notes",
    pages: "28 pages", questions: "52 questions", note: "JEE base",
  },
  {
    id: "n8", cls: "9", subj: "chem", subjLabel: "Chemistry",
    title: "Atoms and Molecules",
    img: "https://images.unsplash.com/photo-1554475901-4538ddfbccc2?auto=format&fit=crop&w=500&q=80",
    alt: "Atoms and molecules notes",
    pages: "24 pages", questions: "44 questions", note: "Mole concept",
  },
  {
    id: "n9", cls: "10", subj: "phy", subjLabel: "Physics",
    title: "Light: Reflection & Refraction",
    img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=500&q=80",
    alt: "Light reflection and refraction notes",
    pages: "32 pages", questions: "60 questions", note: "Board + JEE",
  },
  {
    id: "n10", cls: "10", subj: "math", subjLabel: "Maths",
    title: "Trigonometry & Applications",
    img: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&w=500&q=80",
    alt: "Trigonometry notes",
    pages: "30 pages", questions: "58 questions", note: "Formula sheet",
  },
  {
    id: "n11", cls: "10", subj: "chem", subjLabel: "Chemistry",
    title: "Chemical Reactions & Equations",
    img: "https://images.unsplash.com/photo-1616196334218-1c1e4e6d0e57?auto=format&fit=crop&w=500&q=80",
    alt: "Chemical reactions notes",
    pages: "26 pages", questions: "46 questions", note: "Balancing drills",
  },
  {
    id: "n12", cls: "9", subj: "bio", subjLabel: "Biology",
    title: "The Fundamental Unit of Life",
    img: "https://images.unsplash.com/photo-1530026186672-2cd00ffc50fe?auto=format&fit=crop&w=500&q=80",
    alt: "Cell structure notes",
    pages: "21 pages", questions: "36 questions", note: "Diagrams included",
  },
];

/* `short` is what shows on phones — six "Class N" chips cannot fit one
   row at a readable size, and the full label stays for screen readers */
const NOTE_FILTERS = [
  { key: "all", label: "All Classes", short: "All" },
  { key: "6", label: "Class 6", short: "6" },
  { key: "7", label: "Class 7", short: "7" },
  { key: "8", label: "Class 8", short: "8" },
  { key: "9", label: "Class 9", short: "9" },
  { key: "10", label: "Class 10", short: "10" },
];

const STEPS = [
  {
    title: "Take the entry test",
    desc: "A short diagnostic finds the current level, so practice and revision start at the right depth.",
  },
  {
    title: "Get a study plan",
    desc: "A week-by-week roadmap is generated for the full year, with revision slots already built into it.",
  },
  {
    title: "Learn and practise",
    desc: "AI notes, adaptive practice and a daily challenge keep every concept moving through the year.",
  },
  {
    title: "Track and improve",
    desc: "Every test feeds the analytics dashboard, so weak chapters surface early enough to actually fix them.",
  },
];

const COUNTERS = [
  { to: 6, suffix: "", label: "Practice Modes" },
  { to: 8, suffix: "", label: "Test Types" },
  { to: 6, suffix: "", label: "Learning Modules" },
  { to: 15, suffix: " min", label: "Revision Packs" },
];

/* Initials rather than photographs. The stock portraits these used were
   unrelated Western models, so every card read as a mismatch against its
   Indian name — and a stranger's face beside a specific AIR rank implies a
   real person achieved it. Initials carry the same visual weight without
   either problem. `tone` picks one of four accent gradients. */
/* Hero stage slides. Crossfaded rather than scrolled — the photographs share
   a setting and framing, so sliding between them would read as a jolt where
   a fade reads as a change of detail. */
const HERO_SLIDES = [
  { img: heroStudents1, alt: "Class 6 to 10 students studying together in a classroom" },
  { img: heroStudents2, alt: "IIT Foundation students working through practice questions" },
  { img: heroStudents3, alt: "Students collaborating over a laptop, notes and geometry models" },
];



const TESTIMONIALS = [
  {
    quote:
      "My son used to memorise formulas without understanding them. Two terms in, he explains the derivation to me at dinner. That change alone was worth it.",
    name: "Meera S.", role: "Parent, Class 9",
    img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&q=80",
  },
  {
    quote:
      "The daily plan tells me exactly what to study next, and the revision packs catch the topics I had started forgetting. I stopped wasting time deciding what to do.",
    name: "Arjun T.", role: "Student, Class 10",
    img: "https://images.unsplash.com/photo-1521119989659-a83eee488004?auto=format&fit=crop&w=150&q=80",
  },
  {
    quote:
      "We compared four institutes. This was the only one that showed us chapter-level data instead of just a rank. As a parent, that transparency decided it.",
    name: "Rajesh K.", role: "Parent, Class 8",
    img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=150&q=80",
  },
];

const FAQS = [
  {
    q: "Is Class 6 too early to start foundation learning?",
    a: "Not at all. For Class 6 and 7 it is about building concepts, reasoning and problem-solving habits through clear notes, gentle practice and short revision — not exam pressure. The goal is comfort with the subject.",
  },
  {
    q: "Will this clash with school work?",
    a: "No — everything is self-paced. Students learn, practise and revise whenever they have time, and the syllabus maps to the school calendar, so school tests get easier as the same chapters are covered in more depth.",
  },
  {
    q: "How much time does it take each day?",
    a: "The planner builds three to five short tasks a day from the exam date, target and available study hours — usually 30 to 45 minutes. Missed tasks reschedule automatically, so nothing quietly piles up.",
  },
  {
    q: "How does it decide what to revise?",
    a: "Incorrect answers, skipped questions and low-confidence topics automatically enter a prioritised revision queue, delivered as 10 to 15 minute packs timed to when a concept is about to be forgotten.",
  },
  {
    q: "Can we try it before committing?",
    a: "Yes. Register for free and explore the notes, practice, a mock test and the dashboard. No payment details are needed to start.",
  },
];

/* ------------------------------------------------------------------ */
/* Tiny icon helpers                                                   */
/* ------------------------------------------------------------------ */

const Stroke = ({ children, width = 2, ...rest }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {children}
  </svg>
);

const ArrowRight = ({ width = 2.6 }) => (
  <Stroke width={width}>
    <path d="M5 12h13" />
    <path d="m12 5 7 7-7 7" />
  </Stroke>
);

const CapIcon = () => (
  <Stroke>
    <path d="M22 10 12 5 2 10l10 5 10-5Z" />
    <path d="M6 12.5V17c0 1.1 2.7 2.5 6 2.5s6-1.4 6-2.5v-4.5" />
  </Stroke>
);

const UserPlus = ({ width = 2.2 }) => (
  <Stroke width={width}>
    <path d="M15 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M19 8v6" />
    <path d="M22 11h-6" />
  </Stroke>
);

const Star = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="m12 2 3 6.3 6.9.9-5 4.8 1.2 6.9L12 17.6 5.9 20.9 7.1 14l-5-4.8 6.9-.9L12 2Z" />
  </svg>
);

const SIcon = ({ id }) => (
  <svg>
    <use href={`#iitf-${id}`} xlinkHref={`#iitf-${id}`} />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

class IITFoundationLogin extends Component {
  constructor(props) {
    super(props);

    this.rootRef = React.createRef();
    this.trackRef = React.createRef();
    this.barRef = React.createRef();
    this.topSentinelRef = React.createRef();

    this.observer = null;
    this.autoTimer = null;
    this.heroTimer = null;
    this.reduceMotion = false;

    this.state = {
      /* login (lives in a popup opened from the topbar) */
      loginOpen: false,
      username: "",
      password: "",
      showPassword: false,
      loading: false,
      loginError: "",
      touched: {},

      /* hero stage carousel */
      heroSlide: 0,

      /* notes carousel */
      activeCls: "all",
      canPrev: false,
      canNext: true,

      /* faq */
      openFaq: 0,

      /* back-to-top button */
      showTop: false,

      /* register modal */
      modalOpen: false,
      regDone: false,
      doneName: "there",
      regTouched: false,
      reg: {
        name: "",
        mobile: "",
        email: "",
        password: "",
        class: "",
        school: "",
        city: "",
        consent: false,
      },

      /* registration flow: "form" -> "otp" -> done */
      regStep: "form",
      regBusy: false,
      regError: "",
      otp: "",
      otpError: "",
      otpMask: "",
      otpAttemptsLeft: null,
      resendIn: 0,
    };
  }

  /* ---------------- lifecycle ---------------- */

  componentDidMount() {
    document.title = "IIT Foundation — Kickstart Today. Crack Tomorrow.";

    this.reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.setupReveal();
    this.syncCarousel();
    this.playCarousel();
    this.playHeroSlides();
    this.watchScrollDepth();
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("scroll", this.handlePageScroll, { passive: true });
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    if (this.topObserver) this.topObserver.disconnect();
    this.stopCarousel();
    this.stopHeroSlides();
    this.clearResendCountdown();
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handlePageScroll);
    document.body.style.overflow = "";
  }

  /* Drives the back-to-top button. The window scroll listener only fires
     when the document itself scrolls — if the page is ever mounted inside
     a scrolling wrapper (app shells often do this on mobile) it never
     fires and the button would stay hidden. Watching a tall invisible
     sentinel at the top of the page works whatever element scrolls. */
  watchScrollDepth = () => {
    const sentinel = this.topSentinelRef.current;
    if (!sentinel || !("IntersectionObserver" in window)) return;

    this.topObserver = new IntersectionObserver(
      (entries) => {
        const showTop = !entries[0].isIntersecting;
        if (showTop !== this.state.showTop) this.setState({ showTop });
      },
      { threshold: 0 }
    );
    this.topObserver.observe(sentinel);
  };

  handleResize = () => {
    this.syncCarousel();
  };

  /* show the back-to-top button once the hero is out of the way — sooner
     on phones, where a screenful is much shorter */
  handlePageScroll = () => {
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    const showTop = y > (window.innerWidth <= 760 ? 280 : 420);
    if (showTop !== this.state.showTop) this.setState({ showTop });
  };

  scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: this.reduceMotion ? "auto" : "smooth" });
  };

  /* ---------------- reveal on scroll + count up ---------------- */

  countUp = (el) => {
    if (el.dataset.done) return;
    el.dataset.done = "1";
    const target = parseInt(el.dataset.to, 10) || 0;
    const suffix = el.dataset.suffix || "";
    if (this.reduceMotion) {
      el.textContent = target + suffix;
      return;
    }
    const dur = 1400;
    let start = null;
    const tick = (now) => {
      if (start === null) start = now;
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      el.textContent = Math.round(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  setupReveal = () => {
    const root = this.rootRef.current;
    if (!root) return;
    const items = root.querySelectorAll(".reveal");

    if (!("IntersectionObserver" in window) || this.reduceMotion) {
      items.forEach((el) => el.classList.add("in"));
      root.querySelectorAll(".count").forEach(this.countUp);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const el = e.target;
          // stagger siblings so a grid animates in sequence, not all at once
          const sibs = Array.prototype.slice.call(el.parentNode.children);
          const i = sibs.indexOf(el);
          el.style.transitionDelay = `${Math.min(i, 6) * 80}ms`;
          el.classList.add("in");
          el.querySelectorAll(".count").forEach(this.countUp);
          this.observer.unobserve(el);
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    items.forEach((el) => this.observer.observe(el));
  };

  /* ---------------- notes carousel ---------------- */

  carouselStep = () => {
    const track = this.trackRef.current;
    if (!track) return 280;
    const card = track.querySelector(".ncard");
    if (!card) return 280;
    const gap = parseFloat(getComputedStyle(track).gap) || 15;
    const per = Math.max(1, Math.floor(track.clientWidth / (card.offsetWidth + gap)));
    return (card.offsetWidth + gap) * per;
  };

  maxScroll = () => {
    const track = this.trackRef.current;
    return track ? track.scrollWidth - track.clientWidth : 0;
  };

  syncCarousel = () => {
    const track = this.trackRef.current;
    if (!track) return;
    const max = this.maxScroll();
    const x = track.scrollLeft;

    const canPrev = x >= 4;
    const canNext = x <= max - 4;
    if (canPrev !== this.state.canPrev || canNext !== this.state.canNext) {
      this.setState({ canPrev, canNext });
    }

    const fill = this.barRef.current;
    if (fill) {
      const visible = max > 0 ? track.clientWidth / track.scrollWidth : 1;
      const pct = max > 0 ? x / max : 0;
      fill.style.width = `${Math.max(visible * 100, 12)}%`;
      fill.style.transform = `translateX(${pct * (100 / Math.max(visible, 0.12) - 100)}%)`;
    }
  };

  goCarousel = (dir) => {
    const track = this.trackRef.current;
    if (!track) return;
    track.scrollBy({
      left: dir * this.carouselStep(),
      behavior: this.reduceMotion ? "auto" : "smooth",
    });
  };

  playCarousel = () => {
    if (this.reduceMotion || this.autoTimer) return;
    this.autoTimer = setInterval(() => {
      const track = this.trackRef.current;
      if (!track) return;
      if (track.scrollLeft >= this.maxScroll() - 4) {
        track.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        this.goCarousel(1);
      }
    }, 4000);
  };

  stopCarousel = () => {
    clearInterval(this.autoTimer);
    this.autoTimer = null;
  };

  /* ---------------- hero stage carousel ---------------- */
  /* Slower than the notes strip: this one sits behind the headline, so a
     brisk cycle would pull the eye off the copy. Reduced motion holds the
     first slide rather than swapping without the fade. */

  playHeroSlides = () => {
    if (this.reduceMotion || this.heroTimer || HERO_SLIDES.length < 2) return;
    this.heroTimer = setInterval(() => {
      this.setState((s) => ({ heroSlide: (s.heroSlide + 1) % HERO_SLIDES.length }));
    }, 6000);
  };

  stopHeroSlides = () => {
    clearInterval(this.heroTimer);
    this.heroTimer = null;
  };

  /* A manual pick restarts the clock, so the slide the user chose gets a
     full interval rather than whatever was left of the previous one. */
  goHeroSlide = (i) => {
    this.stopHeroSlides();
    this.setState({ heroSlide: i }, this.playHeroSlides);
  };

  handleArrow = (dir) => {
    this.goCarousel(dir);
    this.stopCarousel();
  };

  handleFilter = (cls) => {
    this.setState({ activeCls: cls }, () => {
      const track = this.trackRef.current;
      if (track) {
        track.scrollTo({ left: 0, behavior: this.reduceMotion ? "auto" : "smooth" });
      }
      setTimeout(this.syncCarousel, 60);
    });
  };

  /* ---------------- login ---------------- */

  handleLoginChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value, loginError: "" });
  };

  togglePassword = () =>
    this.setState((s) => ({ showPassword: !s.showPassword }));

  handleLogin = (e) => {
    e.preventDefault();
    const username = this.state.username.trim();
    const { password } = this.state;

    this.setState({ touched: { username: true, password: true } });
    if (!username || !password) return;

    this.setState({ loading: true, loginError: "" });

    this.login(username, password).catch((error) => {
      const message =
        (error.graphQLErrors &&
          error.graphQLErrors.length &&
          error.graphQLErrors.map((x) => x.message).join(", ")) ||
        "Unable to login. Please check your credentials and try again.";
      this.setState({ loginError: message, loading: false });
    });
  };

  /**
   * Foundation registrants sign in with the mobile or email they registered
   * with. The backend resolves either against its hashed lookup columns.
   */
  login = async (username, password) => {
    const { data } = await this.props.foundationLogin({
      variables: { username, password },
    });
    const res = (data && data.foundationLogin) || {};

    if (res.status !== 1) {
      this.setState({
        loading: false,
        loginError: res.message || "Invalid mobile/email or password",
      });
      return;
    }

    // The IIT Foundation portal is kept separate from the rizee student app:
    // no cookies / localStorage are written, and login lands in the foundation
    // dashboard rather than /student/home. The JWT is held in component state
    // only; persisting a session is deferred until the portal needs it.
    this.foundationAuth = {
      token: res.token,
      refreshToken: res.refreshToken,
      user: res.user || null,
    };
    // In-memory session so the student (id/class/class_num) is available across
    // portal pages (e.g. short notes filters by class) — no cookies/localStorage.
    setFoundationStudent(res.user ? { ...res.user, token: res.token } : null);
    this.setState({ loading: false, loginError: "" });
    // Carry the logged-in student to the dashboard via in-memory router state
    // (no cookies / localStorage). The dashboard reads it for the navbar name.
    this.props.history.push({
      pathname: "/iit-foundation/student-dashboard",
      state: { student: res.user || null },
    });
  };

  /* ---------------- register modal ---------------- */

  /* ---------------- login modal ---------------- */

  openLogin = (e) => {
    if (e) e.preventDefault();
    this.lastFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    this.setState({ loginOpen: true, loginError: "", touched: {} });
  };

  closeLogin = () => {
    document.body.style.overflow = "";
    this.setState({ loginOpen: false, loading: false, loginError: "", touched: {} });
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  handleLoginKey = (e) => {
    if (e.key === "Escape") this.closeLogin();
  };

  /* Register is reachable from inside the login popup, so swap one for the
     other rather than stacking two dialogs. */
  switchToRegister = (e) => {
    if (e) e.preventDefault();
    this.setState({ loginOpen: false });
    this.openModal();
  };

  openModal = (e) => {
    if (e) e.preventDefault();
    this.lastFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    this.setState({
      modalOpen: true,
      regDone: false,
      regTouched: false,
      regStep: "form",
      regError: "",
      otpError: "",
    });
  };

  closeModal = () => {
    document.body.style.overflow = "";
    this.clearResendCountdown();
    this.setState({
      modalOpen: false,
      regDone: false,
      regTouched: false,
      regStep: "form",
      regBusy: false,
      regError: "",
      otp: "",
      otpError: "",
      otpMask: "",
      otpAttemptsLeft: null,
      resendIn: 0,
      reg: {
        name: "",
        mobile: "",
        email: "",
        password: "",
        class: "",
        school: "",
        city: "",
        consent: false,
      },
    });
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  handleModalKey = (e) => {
    if (e.key === "Escape") this.closeModal();
  };

  handleRegChange = (e) => {
    const { name, value, type, checked } = e.target;
    let next = type === "checkbox" ? checked : value;
    if (name === "mobile") next = String(next).replace(/[^0-9]/g, "").slice(0, 10);
    this.setState((s) => ({ reg: { ...s.reg, [name]: next } }));
  };

  /* Step 1 — validate locally, then ask the server for an OTP. */
  handleRegSubmit = async (e) => {
    e.preventDefault();
    const { reg, regBusy } = this.state;
    if (regBusy) return;

    this.setState({ regTouched: true, regError: "" });

    if (!reg.name.trim()) return;
    if (!/^[0-9]{10}$/.test(reg.mobile)) return;
    if (!reg.class) return;
    if (reg.password.length < 6) return;
    if (!reg.consent) return;

    this.setState({ regBusy: true });
    try {
      const { data } = await this.props.sendFoundationOtp({
        variables: {
          params: {
            mobile: reg.mobile,
            email: reg.email.trim() || null,
            channel: "mobile",
          },
        },
      });
      const res = (data && data.sendFoundationOtp) || {};

      if (res.status === 1) {
        this.setState({
          regStep: "otp",
          regBusy: false,
          otp: "",
          otpError: "",
          otpMask: res.target_mask || "",
          otpAttemptsLeft: null,
        });
        this.startResendCountdown(res.resend_after || 60);
        return;
      }

      // Cooldown / hourly cap still means a code is (or was) on its way.
      if (res.status === 2 || res.status === 3) {
        this.setState({
          regStep: "otp",
          regBusy: false,
          otpMask: res.target_mask || "",
          otpError: res.message || "",
        });
        this.startResendCountdown(res.resend_after || 60);
        return;
      }

      this.setState({
        regBusy: false,
        regError: res.message || "Could not send the OTP. Please try again.",
      });
    } catch (err) {
      this.setState({ regBusy: false, regError: this.apiError(err) });
    }
  };

  /* Step 2 — verify the code, then create the registration. */
  handleOtpSubmit = async (e) => {
    e.preventDefault();
    const { reg, otp, regBusy } = this.state;
    if (regBusy) return;

    if (!/^[0-9]{4,8}$/.test(otp)) {
      this.setState({ otpError: "Enter the code we sent you." });
      return;
    }

    this.setState({ regBusy: true, otpError: "" });
    try {
      const { data } = await this.props.verifyFoundationOtp({
        variables: {
          params: {
            mobile: reg.mobile,
            channel: "mobile",
            otp,
          },
        },
      });
      const res = (data && data.verifyFoundationOtp) || {};

      if (!res.verified) {
        this.setState({
          regBusy: false,
          otpError: res.message || "Incorrect OTP.",
          otpAttemptsLeft:
            typeof res.attempts_left === "number" ? res.attempts_left : null,
        });
        return;
      }

      await this.completeRegistration();
    } catch (err) {
      this.setState({ regBusy: false, otpError: this.apiError(err) });
    }
  };

  /* Step 3 — the verified mobile lets the registration through. */
  completeRegistration = async () => {
    const { reg } = this.state;
    try {
      const { data } = await this.props.registerFoundationStudent({
        variables: {
          params: {
            name: reg.name.trim(),
            mobile: reg.mobile,
            email: reg.email.trim() || null,
            password: reg.password,
            grade_id: reg.class,
            school: reg.school.trim() || null,
            city: reg.city.trim() || null,
            consent: !!reg.consent,
          },
        },
      });
      const res = (data && data.registerFoundationStudent) || {};

      if (res.status === 1) {
        this.clearResendCountdown();
        // Straight to the IIT Foundation dashboard. Nothing is written to
        // cookies / localStorage — the newly registered student's name is carried
        // via in-memory router state for the navbar greeting.
        document.body.style.overflow = "";
        this.setState({ regBusy: false, modalOpen: false, regStep: "form" });
        // Resolve the selected class label (reg.class holds the grade_id).
        const grades =
          (this.props.foundationGrades && this.props.foundationGrades.getFoundationGrades) || [];
        const gradeObj = grades.find((g) => String(g.id) === String(reg.class));
        const student = {
          id: res.id,
          name: reg.name.trim(),
          // reg.class is the f_class.id the dropdown submitted — content queries
          // key on it, so it goes into the session as grade_id.
          grade_id: reg.class,
          class: gradeObj ? gradeObj.label : "",
          class_num: gradeObj ? gradeObj.class_num : "",
          school: reg.school.trim() || null,
          // Masked the same way the API masks it, so sessionStorage never holds
          // the plaintext address under a "mask" name.
          email_mask: maskEmail(reg.email) || null,
        };
        setFoundationStudent(student);
        this.props.history.push({
          pathname: "/iit-foundation/student-dashboard",
          state: { student },
        });
        return;
      }

      // 4 = mobile already registered, 5 = email already registered. Send the user
      // back to the form (not the OTP step) so they can fix the duplicate field.
      if (res.status === 4 || res.status === 5) {
        this.clearResendCountdown();
        this.setState({
          regBusy: false,
          regStep: "form",
          otp: "",
          otpError: "",
          regError: res.message || "This account is already registered. Please log in.",
        });
        return;
      }

      this.setState({
        regBusy: false,
        otpError: res.message || "Could not complete registration.",
      });
    } catch (err) {
      this.setState({ regBusy: false, otpError: this.apiError(err) });
    }
  };

  handleResendOtp = async () => {
    if (this.state.resendIn > 0 || this.state.regBusy) return;
    const { reg } = this.state;

    this.setState({ regBusy: true, otpError: "" });
    try {
      const { data } = await this.props.sendFoundationOtp({
        variables: {
          params: {
            mobile: reg.mobile,
            email: reg.email.trim() || null,
            channel: "mobile",
          },
        },
      });
      const res = (data && data.sendFoundationOtp) || {};
      this.setState({
        regBusy: false,
        otp: "",
        otpAttemptsLeft: null,
        otpError: res.status === 1 ? "" : res.message || "",
      });
      this.startResendCountdown(res.resend_after || 60);
    } catch (err) {
      this.setState({ regBusy: false, otpError: this.apiError(err) });
    }
  };

  handleOtpChange = (e) => {
    this.setState({
      otp: String(e.target.value).replace(/[^0-9]/g, "").slice(0, 6),
      otpError: "",
    });
  };

  backToRegForm = () => {
    this.clearResendCountdown();
    this.setState({ regStep: "form", otp: "", otpError: "", regError: "" });
  };

  startResendCountdown = (seconds) => {
    this.clearResendCountdown();
    this.setState({ resendIn: Math.max(0, Math.ceil(seconds)) });
    this.resendTimer = setInterval(() => {
      this.setState(
        (s) => ({ resendIn: s.resendIn - 1 }),
        () => {
          if (this.state.resendIn <= 0) this.clearResendCountdown();
        }
      );
    }, 1000);
  };

  clearResendCountdown = () => {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  };

  apiError = (error) =>
    (error &&
      error.graphQLErrors &&
      error.graphQLErrors.length &&
      error.graphQLErrors.map((x) => x.message).join(", ")) ||
    "Something went wrong. Please try again.";

  /* ---------------- render helpers ---------------- */

  renderSprite() {
    return (
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <symbol id="iitf-ic-thermo" viewBox="0 0 24 24">
          <path d="M14 14.8V4a2 2 0 0 0-4 0v10.8a4.5 4.5 0 1 0 4 0Z" />
          <path d="M12 9v7.5" />
          <path d="M15.5 6h-2M15.5 9h-2M15.5 12h-2" />
        </symbol>
        <symbol id="iitf-ic-flask" viewBox="0 0 24 24">
          <path d="M9 2h6" />
          <path d="M10 2v6.5L4.6 18A2 2 0 0 0 6.3 21h11.4a2 2 0 0 0 1.7-3L14 8.5V2" />
          <path d="M7.5 15h9" />
          <circle cx="10.5" cy="17.5" r=".9" />
          <circle cx="14" cy="18.5" r=".7" />
        </symbol>
        <symbol id="iitf-ic-atom" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="2" />
          <ellipse cx="12" cy="12" rx="10" ry="4.2" />
          <ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(60 12 12)" />
          <ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(120 12 12)" />
        </symbol>
        <symbol id="iitf-ic-compass" viewBox="0 0 24 24">
          <circle cx="12" cy="4" r="2" />
          <path d="M12 6v3" />
          <path d="M12 9 6 21" />
          <path d="m12 9 6 12" />
          <path d="M8.4 16h7.2" />
        </symbol>
        <symbol id="iitf-ic-calc" viewBox="0 0 24 24">
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <rect x="7" y="5" width="10" height="4" rx="1" />
          <path d="M8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01" />
        </symbol>
        <symbol id="iitf-ic-micro" viewBox="0 0 24 24">
          <path d="M6 22h14" />
          <path d="M9 22a6 6 0 0 0 6-10" />
          <path d="M9 15h4" />
          <path d="M12 6 9.5 9.5" />
          <path d="m14 3-4 5.5 3 2L17 5.5 14 3Z" />
          <path d="M7 18h4" />
        </symbol>
        <symbol id="iitf-ic-dna" viewBox="0 0 24 24">
          <path d="M5 2c0 6 14 6 14 12s-14 6-14 8" />
          <path d="M19 2c0 6-14 6-14 12s14 6 14 8" />
          <path d="M7.5 6h9M6 10h12M6 14h12M7.5 18h9" />
        </symbol>
        <symbol id="iitf-ic-magnet" viewBox="0 0 24 24">
          <path d="M5 4v9a7 7 0 0 0 14 0V4h-5v9a2 2 0 0 1-4 0V4H5Z" />
          <path d="M5 8h5M14 8h5" />
        </symbol>
        <symbol id="iitf-ic-ruler" viewBox="0 0 24 24">
          <path d="M3 20V5l16 15H3Z" />
          <path d="M3 15h4M3 11h3M7 20v-4M11 20v-3" />
        </symbol>
        <symbol id="iitf-ic-cap" viewBox="0 0 24 24">
          <path d="M22 10 12 5 2 10l10 5 10-5Z" />
          <path d="M6 12.5V17c0 1.2 2.7 2.6 6 2.6s6-1.4 6-2.6v-4.5" />
          <path d="M22 10v5" />
        </symbol>
        <symbol id="iitf-ic-book" viewBox="0 0 24 24">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
          <path d="M9 7h7M9 11h5" />
        </symbol>
        <symbol id="iitf-ic-bulb" viewBox="0 0 24 24">
          <path d="M9 18h6" />
          <path d="M10 22h4" />
          <path d="M12 2a7 7 0 0 0-4 12.7V18h8v-3.3A7 7 0 0 0 12 2Z" />
        </symbol>
      </svg>
    );
  }

  renderLoginForm() {
    const { username, password, showPassword, loading, loginError, touched } =
      this.state;

    const badUser = touched.username && !username.trim();
    const badPass = touched.password && !password;

    return (
      <div className="mbody">
        <form onSubmit={this.handleLogin} noValidate>
          {loginError ? (
            <p className="merror" role="alert">
              {loginError}
            </p>
          ) : null}

          <div className={`fld ${badUser ? "is-invalid" : ""}`}>
            <label htmlFor="iitf-lg-user">
              Mobile Number / Email <span>*</span>
            </label>
            <input
              id="iitf-lg-user"
              name="username"
              type="text"
              placeholder="Registered mobile or email"
              autoComplete="username"
              value={username}
              onChange={this.handleLoginChange}
              autoFocus
            />
            {badUser && <span className="fhint">Please enter your mobile number or email.</span>}
          </div>

          <div className={`fld pw-fld ${badPass ? "is-invalid" : ""}`}>
            <label htmlFor="iitf-lg-pass">
              Password <span>*</span>
            </label>
            <input
              id="iitf-lg-pass"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Your password"
              autoComplete="current-password"
              value={password}
              onChange={this.handleLoginChange}
            />
            <button
              type="button"
              className="pw-eye"
              onClick={this.togglePassword}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
            >
              {showPassword ? (
                <Stroke>
                  <path d="M2 12s3.6-7 10-7c2 0 3.7.7 5.1 1.6" />
                  <path d="M21.5 10.5c.3.6.5 1.1.5 1.5 0 0-3.6 7-10 7-1.6 0-3-.4-4.2-1" />
                  <path d="m3 3 18 18" />
                </Stroke>
              ) : (
                <Stroke>
                  <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </Stroke>
              )}
            </button>
            {badPass && <span className="fhint">Please enter your password.</span>}
          </div>

          <button className="msubmit" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spin" aria-hidden="true" />
                Signing in…
              </>
            ) : (
              <>
                Login
                <ArrowRight width={2.4} />
              </>
            )}
          </button>
        </form>

        <p className="mnote">
          New to IIT Foundation?{" "}
          <a href="#register" onClick={this.switchToRegister}>
            <b>Register Now</b>
          </a>
        </p>
      </div>
    );
  }

  renderLoginModal() {
    if (!this.state.loginOpen) return null;

    return (
      <div
        className="rgmodal lgmodal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="iitf-lg-title"
        onKeyDown={this.handleLoginKey}
      >
        <div className="rgmodal-bd" onClick={this.closeLogin} />

        <div className="rgmodal-card">
          <div className="mhead">
            <button
              className="mclose"
              type="button"
              onClick={this.closeLogin}
              aria-label="Close login"
            >
              <Stroke width={2.6}>
                <path d="M18 6 6 18M6 6l12 12" />
              </Stroke>
            </button>
            <div className="mh-in">
              <span className="mi">
                <CapIcon />
              </span>
              <div>
                <h3 id="iitf-lg-title">Login</h3>
                <p>Welcome back! Let's keep learning 👋</p>
              </div>
            </div>
          </div>

          {this.renderLoginForm()}
        </div>
      </div>
    );
  }

  renderNotes() {
    const { activeCls, canPrev, canNext } = this.state;
    const visible = NOTES.filter((n) => activeCls === "all" || n.cls === activeCls);

    return (
      <section className="sec notes">
        <span className="dots d-md dc-blue fade-r sway" style={{ left: "-12px", top: "26px" }} />
        <span className="dots d-sm dc-orange fade-l" style={{ right: "-8px", bottom: "26px" }} />
        <span className="sicon si-lg sc-teal slow" style={{ left: "4%", top: "34%" }}>
          <SIcon id="ic-book" />
        </span>
        <span className="sicon si-md sc-violet rev" style={{ right: "4%", top: "28%" }}>
          <SIcon id="ic-bulb" />
        </span>

        <div className="sec-head reveal">
          <span className="eyebrow">
            <Stroke width={2.4}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
              <path d="M14 2v6h6" />
              <path d="M8 13h8M8 17h5" />
            </Stroke>
            Study Material
          </span>
          <h2>
            Chapter notes for <span className="accent">Class 6 to 10</span>
          </h2>
          <p>
            Every chapter condensed into a printable PDF — definitions, derivations, solved
            examples and a practice set at the end. Free for enrolled students.
          </p>
        </div>

        <div className="nstats reveal">
          <div className="nstat">
            <b>310+</b>
            <span>Chapter Notes</span>
          </div>
          <div className="nstat">
            <b>5</b>
            <span>Classes Covered</span>
          </div>
          <div className="nstat">
            <b>4</b>
            <span>Subjects</span>
          </div>
        </div>

        <div className="nfilter reveal">
          {NOTE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`nchip ${activeCls === f.key ? "on" : ""}`}
              aria-label={f.label}
              aria-pressed={activeCls === f.key}
              onClick={() => this.handleFilter(f.key)}
            >
              <span className="nchip-full">{f.label}</span>
              <span className="nchip-short">{f.short}</span>
            </button>
          ))}
        </div>

        <div
          className="ncarousel"
          onMouseEnter={this.stopCarousel}
          onMouseLeave={this.playCarousel}
          onFocus={this.stopCarousel}
          onTouchStart={this.stopCarousel}
        >
          <button
            className="narrow prev"
            type="button"
            aria-label="Previous notes"
            disabled={!canPrev}
            onClick={() => this.handleArrow(-1)}
          >
            <Stroke width={2.6}>
              <path d="m15 18-6-6 6-6" />
            </Stroke>
          </button>
          <button
            className="narrow next"
            type="button"
            aria-label="More notes"
            disabled={!canNext}
            onClick={() => this.handleArrow(1)}
          >
            <Stroke width={2.6}>
              <path d="m9 18 6-6-6-6" />
            </Stroke>
          </button>

          <div className="ntrack" ref={this.trackRef} onScroll={this.syncCarousel}>
            {visible.map((n, i) => (
              <article
                className="ncard"
                key={`${activeCls}-${n.id}`}
                style={{ animationDelay: `${Math.min(i, 8) * 45}ms` }}
              >
                <div className="nimg">
                  <span className={`nsub ${n.subj}`}>{n.subjLabel}</span>
                  <span className="ncls">Class {n.cls}</span>
                  <img src={n.img} alt={n.alt} />
                </div>
                <div className="nbody">
                  <h3>{n.title}</h3>
                  <div className="nmeta">
                    <span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16v16H4z" />
                        <path d="M8 9h8M8 13h5" />
                      </svg>
                      {n.pages}
                    </span>
                    <span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m9 11 2 2 4-4" />
                        <circle cx="12" cy="12" r="9" />
                      </svg>
                      {n.questions}
                    </span>
                    <span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                      </svg>
                      {n.note}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="nprog">
          <i ref={this.barRef} />
        </div>
      </section>
    );
  }

  renderModal() {
    const {
      modalOpen, regDone, doneName, reg, regTouched,
      regStep, regBusy, regError, otp, otpError, otpMask, otpAttemptsLeft, resendIn,
    } = this.state;
    if (!modalOpen) return null;

    const gradesQuery = this.props.foundationGrades || {};
    const serverGrades = gradesQuery.getFoundationGrades;
    // Use the server list once it loads; until then (or if it errors) fall back
    // to the static classes so the dropdown is never empty.
    const grades =
      serverGrades && serverGrades.length ? serverGrades : FALLBACK_GRADES;

    const badName = regTouched && !reg.name.trim();
    const badMobile = regTouched && !/^[0-9]{10}$/.test(reg.mobile);
    const badClass = regTouched && !reg.class;
    const badPassword = regTouched && reg.password.length < 6;
    const badConsent = regTouched && !reg.consent;

    return (
      <div
        className="rgmodal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="iitf-reg-title"
        onKeyDown={this.handleModalKey}
      >
        <div className="rgmodal-bd" onClick={this.closeModal} />

        <div className="rgmodal-card">
          <div className="mhead">
            <button
              className="mclose"
              type="button"
              onClick={this.closeModal}
              aria-label="Close registration form"
            >
              <Stroke width={2.6}>
                <path d="M18 6 6 18M6 6l12 12" />
              </Stroke>
            </button>
            <div className="mh-in">
              <span className="mi">
                <CapIcon />
              </span>
              <div>
                <h3 id="iitf-reg-title">
                  {regStep === "otp" ? "Verify your mobile" : "Register"}
                </h3>
                {regStep === "otp" ? (
                  <p>Enter the 6-digit code we sent to {otpMask || "your mobile"}.</p>
                ) : (
                  <p>Start your free trial 🚀</p>
                )}
              </div>
            </div>
          </div>

          {!regDone && regStep === "otp" ? (
            <div className="mbody">
              <form onSubmit={this.handleOtpSubmit} noValidate>
                <div className={`fld otp-fld ${otpError ? "is-invalid" : ""}`}>
                  <label htmlFor="iitf-rf-otp">
                    Verification Code <span>*</span>
                  </label>
                  <input
                    id="iitf-rf-otp"
                    name="otp"
                    type="text"
                    className="otp-input"
                    placeholder="------"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength="6"
                    value={otp}
                    onChange={this.handleOtpChange}
                    autoFocus
                  />
                </div>

                {otpError ? (
                  <p className="merror" role="alert">
                    {otpError}
                    {typeof otpAttemptsLeft === "number" && otpAttemptsLeft > 0
                      ? ` ${otpAttemptsLeft} attempt${otpAttemptsLeft === 1 ? "" : "s"} left.`
                      : ""}
                  </p>
                ) : null}

                <button className="msubmit" type="submit" disabled={regBusy}>
                  {regBusy ? "Verifying…" : "Verify & Register"}
                  {!regBusy && <ArrowRight width={2.5} />}
                </button>

                <div className="motp-actions">
                  <button type="button" className="mlink" onClick={this.backToRegForm}>
                    Change details
                  </button>
                  <button
                    type="button"
                    className="mlink"
                    onClick={this.handleResendOtp}
                    disabled={resendIn > 0 || regBusy}
                  >
                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                  </button>
                </div>

                <p className="mnote">
                  The code is valid for <b>10 minutes</b>. Standard SMS charges may apply.
                </p>
              </form>
            </div>
          ) : !regDone ? (
            <div className="mbody">
              <form onSubmit={this.handleRegSubmit} noValidate>
                <div className="mgrid">
                  <div className={`fld span2 ${badName ? "is-invalid" : ""}`}>
                    <label htmlFor="iitf-rf-name">
                      Student Name <span>*</span>
                    </label>
                    <input
                      id="iitf-rf-name"
                      name="name"
                      type="text"
                      placeholder="Full name"
                      autoComplete="name"
                      value={reg.name}
                      onChange={this.handleRegChange}
                      autoFocus
                    />
                  </div>

                  <div className={`fld ${badMobile ? "is-invalid" : ""}`}>
                    <label htmlFor="iitf-rf-mobile">
                      Mobile Number <span>*</span>
                    </label>
                    <input
                      id="iitf-rf-mobile"
                      name="mobile"
                      type="tel"
                      placeholder="10-digit number"
                      inputMode="numeric"
                      maxLength="10"
                      autoComplete="tel"
                      value={reg.mobile}
                      onChange={this.handleRegChange}
                    />
                  </div>

                  <div className="fld">
                    <label htmlFor="iitf-rf-email">Email</label>
                    <input
                      id="iitf-rf-email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      value={reg.email}
                      onChange={this.handleRegChange}
                    />
                  </div>

                  <div className={`fld span2 ${badPassword ? "is-invalid" : ""}`}>
                    <label htmlFor="iitf-rf-pwd">
                      Password <span>*</span>
                    </label>
                    <input
                      id="iitf-rf-pwd"
                      name="password"
                      type="password"
                      placeholder="At least 6 characters"
                      autoComplete="new-password"
                      value={reg.password}
                      onChange={this.handleRegChange}
                    />
                    <small className="fhint">
                      Use this with your mobile or email to log in.
                    </small>
                  </div>

                  <div className={`fld ${badClass ? "is-invalid" : ""}`}>
                    <label htmlFor="iitf-rf-class">
                      Current Class <span>*</span>
                    </label>
                    <select
                      id="iitf-rf-class"
                      name="class"
                      value={reg.class}
                      onChange={this.handleRegChange}
                    >
                      <option value="">Select class</option>
                      {grades.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="fld">
                    <label htmlFor="iitf-rf-school">School</label>
                    <input
                      id="iitf-rf-school"
                      name="school"
                      type="text"
                      placeholder="e.g. Delhi Public School"
                      value={reg.school}
                      onChange={this.handleRegChange}
                    />
                  </div>

                  <div className="fld span2">
                    <label htmlFor="iitf-rf-city">City</label>
                    <input
                      id="iitf-rf-city"
                      name="city"
                      type="text"
                      placeholder="e.g. Hyderabad"
                      value={reg.city}
                      onChange={this.handleRegChange}
                    />
                  </div>
                </div>

                <label className={`mconsent ${badConsent ? "is-invalid" : ""}`}>
                  <input
                    type="checkbox"
                    name="consent"
                    checked={reg.consent}
                    onChange={this.handleRegChange}
                  />
                  <span>
                    I agree to be contacted by IIT Foundation about batches, schedules and fee
                    details.
                  </span>
                </label>

                {regError ? (
                  <p className="merror" role="alert">
                    {regError}
                  </p>
                ) : null}

                <button className="msubmit" type="submit" disabled={regBusy}>
                  {regBusy ? "Sending OTP…" : "Register Free"}
                  {!regBusy && <ArrowRight width={2.5} />}
                </button>

                <p className="mnote">
                  We will text a verification code to confirm your number. No payment details
                  needed — our team calls within <b>24 hours</b> to confirm the batch.
                </p>
              </form>
            </div>
          ) : (
            <div className="msuccess">
              <div className="tick">
                <svg viewBox="0 0 24 24">
                  <path d="m4 12.5 5.5 5.5L20 7" />
                </svg>
              </div>
              <h4>You are registered</h4>
              <p>
                Thanks, <b>{doneName}</b>. Your free trial week is reserved — our team will
                call on the number you gave us within <b>24 hours</b> to confirm the batch and
                share the class link.
              </p>
              <button className="mdone-btn" type="button" onClick={this.closeModal}>
                Back to site
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { openFaq } = this.state;

    return (
      <div className="iitf-page fdn-theme" ref={this.rootRef}>
        {/* scroll-depth marker for the back-to-top button */}
        <span className="top-sentinel" ref={this.topSentinelRef} aria-hidden="true" />
        {this.renderSprite()}

        {/* Sticky header. It sits outside .frame on purpose — both .frame and
            .lpane clip with overflow: hidden, and position: sticky cannot
            escape a clipping ancestor, so nested here it would never stick. */}
        <CommonHeader className="topbar" color="auto" tone="light" showUserMenu={false} container={false} left={<React.Fragment>
          {/* Rizee co-branding */}
          <a className="powered" href="https://rizee.in" aria-label="Rizee">
            <BrandLogo fill="#ffffff" />
          </a>

          {/* Both actions live in one group so they stay side by side on the
              right — the topbar switches to space-between on tablet, which
              would otherwise push them apart. */}
          <div className="tb-actions">
            <a className="btn-signin" href="#login" onClick={this.openLogin}>
              <Stroke width={2.2}>
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <path d="M10 17l5-5-5-5" />
                <path d="M15 12H3" />
              </Stroke>
              Login
            </a>

            <a className="btn-register" href="#register" onClick={this.openModal}>
              <UserPlus />
              Register
            </a>
          </div>
        </React.Fragment>} />

        <div className="frame">
          <div className="app">
            {/* ==================== LEFT ==================== */}
            <div className="lpane">
              {/* body */}
              <div className="left-body">
                <span
                  className="dots d-md dc-red fade-b"
                  style={{ left: "-14px", top: "-6px", opacity: 0.75 }}
                />
                <span
                  className="dots d-sm dc-teal fade-t sway"
                  style={{ left: "6px", bottom: "6px", opacity: 0.8 }}
                />

                {/* hero */}
                <div className="hero">
                  <div>
                    {/* Programme lockup, moved here from the removed right
                        panel — with that gone this is the only place the
                        IIT Foundation name and class range appear. */}
                    <span className="hlock">
                      <span className="hlock-mark">
                        <svg
                          viewBox="0 0 24 24"
                          width="21"
                          height="21"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M22 10 12 5 2 10l10 5 10-5Z" />
                          <path d="M6 12.5V17c0 1.1 2.7 2.5 6 2.5s6-1.4 6-2.5v-4.5" />
                        </svg>
                      </span>
                      <span className="hlock-tx">
                        <b>
                          IIT <em>FOUNDATION</em>
                        </b>
                        <small>Kickstart Today. Crack Tomorrow.</small>
                      </span>
                      <span className="hlock-cls">Class 6 – 10</span>
                    </span>

                    <span className="eyebrow hbrand">
                      <i /> Now Enrolling
                    </span>
                    <h1>
                      Build Strong Concepts.
                      <br />
                      Practice Smart.
                      <br />
                      <span className="accent">Achieve Excellence.</span>
                    </h1>
                    <p className="lede">
                      India's most trusted foundation program for <b>IIT-JEE, Olympiads</b> &amp;
                      beyond.
                    </p>

                    <div className="flist">
                      {HERO_FEATURES.map((f) => (
                        <div className={`fcard ${f.tone}`} key={f.title}>
                          <div className={`fi ${f.tone}`}>
                            <Stroke>{f.path}</Stroke>
                          </div>
                          <div>
                            <b>{f.title}</b>
                            <span>{f.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* photo stage, framed as a tilted card with stickers */}
                  <div className="stage">
                    {/* playful stickers around the photo — decoration only */}
                    <span className="sticker st-1" aria-hidden="true">
                      <i>🧪</i> Science
                    </span>
                    <span className="sticker st-2" aria-hidden="true">
                      <i>📐</i> Maths
                    </span>
                    <span className="sticker st-3" aria-hidden="true">
                      <i>🏆</i> Olympiad ready
                    </span>

                    <div className="student">
                      {HERO_SLIDES.map((s, i) => (
                        <img
                          key={s.img}
                          src={s.img}
                          alt={i === this.state.heroSlide ? s.alt : ""}
                          className={i === this.state.heroSlide ? "on" : ""}
                          aria-hidden={i !== this.state.heroSlide}
                        />
                      ))}
                    </div>

                    {HERO_SLIDES.length > 1 && (
                      <div className="hdots">
                        {HERO_SLIDES.map((s, i) => (
                          <button
                            type="button"
                            key={s.img}
                            className={`hdot ${i === this.state.heroSlide ? "on" : ""}`}
                            aria-label={`Show image ${i + 1} of ${HERO_SLIDES.length}`}
                            aria-current={i === this.state.heroSlide}
                            onClick={() => this.goHeroSlide(i)}
                          />
                        ))}
                      </div>
                    )}

                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* ==================== TRUST STRIP ====================
              Its own full-width band rather than sitting inside the left
              pane, so the four cards span the page instead of being
              squeezed beside the login panel. */}
          <div className="trustband">
            <div className="trust">
              {TRUST.map((t) => (
                <div className={`titem ${t.tone} reveal`} key={t.title}>
                  <div className="ti">
                    <Stroke>{t.path}</Stroke>
                  </div>
                  <div className="tx">
                    <b>{t.title}</b>
                    <span>{t.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ==================== WHY CHOOSE ==================== */}
          <section className="sec why">
            <span className="dots d-lg dc-red fade-r sway" style={{ left: "-18px", top: "26px" }} />
            <span className="dots d-md dc-teal fade-l" style={{ right: "-14px", bottom: "34px" }} />
            <span className="sicon si-lg sc-blue" style={{ left: "3%", top: "30%" }}>
              <SIcon id="ic-thermo" />
            </span>
            <span className="sicon si-md sc-orange slow" style={{ right: "4%", top: "22%" }}>
              <SIcon id="ic-flask" />
            </span>
            <span className="sicon si-md sc-teal rev" style={{ left: "7%", bottom: "14%" }}>
              <SIcon id="ic-dna" />
            </span>
            <span className="sicon si-lg sc-violet slow" style={{ right: "6%", bottom: "12%" }}>
              <SIcon id="ic-atom" />
            </span>

            <div className="sec-head reveal">
              <span className="eyebrow">
                <Stroke width={2.4}>
                  <path d="m12 3 2.6 6.3 6.8.5-5.2 4.4 1.6 6.6L12 17.3 6.2 20.8l1.6-6.6L2.6 9.8l6.8-.5L12 3Z" />
                </Stroke>
                Why IIT Foundation
              </span>
              <h2>
                Built for students who want to <span className="accent">start early</span>
              </h2>
              <p>
                A foundation program designed around one idea — if the basics are strong in Class 6
                to 10, Class 11 and 12 stop being a struggle.
              </p>
            </div>

            <div className="wgrid">
              {WHY.map((w) => (
                <div
                  className="wcard reveal"
                  key={w.title}
                  style={{ "--ac": w.ac, "--acbg": w.acbg, "--acbd": w.acbd }}
                >
                  <div className="wi">
                    <Stroke>{w.path}</Stroke>
                  </div>
                  <h3>{w.title}</h3>
                  <p>{w.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ==================== SUBJECTS ==================== */}
          <section className="sec subjects">
            <span className="fcircle fc1" />
            <span className="fcircle fc2" />
            <span className="fcircle fc3" />
            <span className="fcircle fc4" />
            <span className="fcircle fc5" />
            <span className="dots d-md dc-violet fade-r" style={{ left: "-12px", top: "34px" }} />
            <span
              className="dots d-lg dc-teal fade-l sway"
              style={{ right: "-16px", bottom: "20px" }}
            />
            <span className="sicon si-md sc-blue" style={{ left: "5%", top: "20%" }}>
              <SIcon id="ic-micro" />
            </span>
            <span className="sicon si-lg sc-orange slow" style={{ right: "4%", top: "26%" }}>
              <SIcon id="ic-magnet" />
            </span>
            <span className="sicon si-md sc-violet rev" style={{ left: "9%", bottom: "10%" }}>
              <SIcon id="ic-bulb" />
            </span>

            <div className="sec-head reveal">
              <span className="eyebrow">
                <Stroke width={2.4}>
                  <circle cx="12" cy="12" r="3" />
                  <ellipse cx="12" cy="12" rx="10" ry="4.5" />
                  <ellipse cx="12" cy="12" rx="10" ry="4.5" transform="rotate(60 12 12)" />
                </Stroke>
                Subjects We Cover
              </span>
              <h2>
                Every subject, taught to <span className="accent">real depth</span>
              </h2>
              <p>
                In-depth coverage across every subject — the ring shows how much of each
                subject's yearly syllabus is completed.
              </p>
            </div>

            <div className="sgrid">
              {SUBJECTS.map((s) => (
                <div
                  className="scard reveal"
                  key={s.title}
                  style={{ "--ac": s.ac, "--acbg": s.acbg, "--acbd": s.acbd, "--off": s.off }}
                >
                  <div className="ring">
                    <svg className="prog" viewBox="0 0 106 106">
                      <circle className="trk" cx="53" cy="53" r="47" />
                      <circle className="bar" cx="53" cy="53" r="47" />
                    </svg>
                    <span className="orbit">
                      <i />
                    </span>
                    <span className="pic">
                      <img src={s.img} alt={s.title} />
                    </span>
                  </div>
                  <h3>{s.title}</h3>
                  <div className="pct">{s.pct}</div>
                  <p>{s.desc}</p>
                  <div className="stags">
                    {s.tags.map((t) => (
                      <span key={t}>{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ==================== STUDY NOTES ==================== */}
          {this.renderNotes()}

          {/* ==================== LEARNING PATH ==================== */}
          <section className="sec path">
            <span className="dots d-md dc-teal fade-r" style={{ left: "-12px", top: "28px" }} />
            <span className="dots d-sm dc-red fade-l sway" style={{ right: "-8px", bottom: "30px" }} />
            <span className="sicon si-lg sc-blue slow" style={{ left: "4%", top: "26%" }}>
              <SIcon id="ic-cap" />
            </span>
            <span className="sicon si-md sc-teal rev" style={{ right: "5%", top: "22%" }}>
              <SIcon id="ic-thermo" />
            </span>

            <div className="sec-head reveal">
              <span className="eyebrow">
                <Stroke width={2.4}>
                  <path d="M12 2v20" />
                  <path d="m5 9 7-7 7 7" />
                </Stroke>
                How It Works
              </span>
              <h2>
                Four steps from <span className="accent">enrolment to results</span>
              </h2>
              <p>
                No complicated onboarding. A student can register and start their first practice set in minutes.
              </p>
            </div>

            <div className="steps">
              {STEPS.map((s, i) => (
                <div className="step reveal" key={s.title}>
                  <div className="num">{i + 1}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ==================== RESULTS ==================== */}
          <section className="sec results">
            <span className="dots d-lg dc-white fade-r" style={{ left: "-16px", top: "30px" }} />
            <span
              className="dots d-md dc-white fade-l sway"
              style={{ right: "-12px", bottom: "26px" }}
            />
            <span className="sicon si-lg sc-light" style={{ left: "4%", top: "24%" }}>
              <SIcon id="ic-atom" />
            </span>
            <span className="sicon si-md sc-light2 slow" style={{ right: "5%", top: "20%" }}>
              <SIcon id="ic-flask" />
            </span>
            <span className="sicon si-md sc-light rev" style={{ left: "8%", bottom: "12%" }}>
              <SIcon id="ic-calc" />
            </span>
            <span className="sicon si-lg sc-light2 slow" style={{ right: "6%", bottom: "10%" }}>
              <SIcon id="ic-dna" />
            </span>

            <div className="sec-head reveal">
              <span className="eyebrow">
                <Stroke width={2.4}>
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                  <path d="M6 4h12v5a6 6 0 0 1-12 0V4Z" />
                  <path d="M9 21h6" />
                  <path d="M12 15v6" />
                </Stroke>
                The Platform
              </span>
              <h2>
                Everything in <span className="accent">one guided plan</span>
              </h2>
              <p>Notes, practice, tests and revision — one connected system that guides each day.</p>
            </div>

            <div className="cgrid">
              {COUNTERS.map((c) => (
                <div className="cbox reveal" key={c.label}>
                  <b className="count" data-to={c.to} data-suffix={c.suffix}>
                    0
                  </b>
                  <span>{c.label}</span>
                </div>
              ))}
            </div>

          </section>

          {/* ==================== TESTIMONIALS ==================== */}
          <section className="sec">
            <span className="dots d-md dc-red fade-l" style={{ right: "-12px", top: "26px" }} />
            <span className="sicon si-md sc-orange" style={{ left: "4%", top: "24%" }}>
              <SIcon id="ic-bulb" />
            </span>
            <span className="sicon si-lg sc-blue slow" style={{ right: "4%", top: "28%" }}>
              <SIcon id="ic-book" />
            </span>

            <div className="sec-head reveal">
              <span className="eyebrow">
                <Stroke width={2.4}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
                </Stroke>
                What Families Say
              </span>
              <h2>
                Students and parents on the <span className="accent">difference it made</span>
              </h2>
            </div>

            <div className="tgrid">
              {TESTIMONIALS.map((t) => (
                <div className="tcard reveal" key={t.name}>
                  <div className="qm">“</div>
                  <p>{t.quote}</p>
                  <div className="tmeta">
                    <span className="ta">
                      <img src={t.img} alt={t.name} />
                    </span>
                    <div>
                      <b>{t.name}</b>
                      <small>{t.role}</small>
                    </div>
                    <span className="stars">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <Star key={i} />
                      ))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ==================== FAQ ==================== */}
          <section className="sec why">
            {/* Sole left-edge grid across this boundary — the testimonials
                section no longer stacks one directly above it. */}
            <span className="dots d-lg dc-teal fade-r" style={{ left: "-18px", top: "10%" }} />
            <span
              className="dots d-md dc-orange fade-l sway"
              style={{ right: "-12px", bottom: "28px" }}
            />
            <span className="sicon si-lg sc-violet slow" style={{ left: "4%", top: "30%" }}>
              <SIcon id="ic-compass" />
            </span>
            <span className="sicon si-md sc-red rev" style={{ right: "5%", top: "26%" }}>
              <SIcon id="ic-magnet" />
            </span>
            <span className="sicon si-md sc-teal" style={{ left: "7%", bottom: "14%" }}>
              <SIcon id="ic-ruler" />
            </span>

            <div className="sec-head reveal">
              <span className="eyebrow">
                <Stroke width={2.4}>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.5 9a2.5 2.5 0 1 1 3.4 2.3c-.6.3-.9.8-.9 1.4" />
                  <path d="M12 17h.01" />
                </Stroke>
                Common Questions
              </span>
              <h2>
                Everything parents ask us <span className="accent">before joining</span>
              </h2>
            </div>

            {/* The open state rides on a data attribute, not a class. The
                scroll-reveal observer adds `in` to each card imperatively, and
                a className that changes between renders makes React rewrite the
                whole class attribute — which would strip `in` and fade the card
                back out to opacity 0. */}
            <div className="faq-wrap">
              {FAQS.map((f, i) => (
                <div className="faq reveal" data-open={openFaq === i} key={f.q}>
                  <button
                    type="button"
                    className="q"
                    aria-expanded={openFaq === i}
                    onClick={() => this.setState({ openFaq: openFaq === i ? -1 : i })}
                  >
                    {f.q}
                    <span className="plus">
                      <Stroke width={3}>
                        <path d="M12 5v14M5 12h14" />
                      </Stroke>
                    </span>
                  </button>
                  {openFaq === i && <div className="ans">{f.a}</div>}
                </div>
              ))}
            </div>
          </section>

          {/* ==================== CLOSING CTA ==================== */}
          <section className="cta">
            <div className="cta-in reveal">
              <span
                className="dots d-md dc-white fade-r"
                style={{ left: "14px", top: "12px", zIndex: 1 }}
              />
              <span
                className="dots d-sm dc-white fade-l sway"
                style={{ right: "16px", bottom: "10px", zIndex: 1 }}
              />
              <span className="sicon si-lg sc-light slow" style={{ right: "22%", top: "14%" }}>
                <SIcon id="ic-cap" />
              </span>
              <span className="sicon si-md sc-light2 rev" style={{ left: "38%", bottom: "12%" }}>
                <SIcon id="ic-atom" />
              </span>

              <div className="cta-tx">
                <h2>
                  Start the foundation year <span className="accent">the right way</span>
                </h2>
                <p>Free trial week · No payment details needed · Seats limited per batch</p>
              </div>
              <div className="cta-btns">
                <a className="cbtn solid" href="#register" onClick={this.openModal}>
                  <UserPlus width={2.3} />
                  Register Free
                </a>
                <a
                  className="cbtn ghost"
                  href="https://api.whatsapp.com/send?phone=919121218365&text=Hi"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2a10 10 0 0 0-8.6 15L2 22l5.1-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.4-.7-1.7-.8-.2-.1-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.1-.2 0-.4.1-.5l.4-.5c.1-.2.2-.3.3-.5v-.4l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.9.7 1.9.5 2.3.5a2.7 2.7 0 0 0 1.8-1.3 2.2 2.2 0 0 0 .2-1.3c-.1-.1-.2-.1-.4-.2Z" />
                  </svg>
                  Talk to Our Team
                </a>
              </div>
            </div>
          </section>

          {/* ==================== FOOTER ==================== */}
          <footer>
            <a className="f-rizee" href="https://rizee.in">
              <img src={rizeeLogoWhite} alt="Rizee — The Perfect Guide" />
            </a>

            <div className="copy">
              © 2026 IIT Foundation · A <b>Rizee</b> product. All Rights Reserved.
            </div>

            {/* Official Rizee channels. rel="noopener noreferrer" because each
                opens in a new tab — without it the opened page can reach back
                through window.opener. */}
            <div className="fsocial">
              <a
                className="s-yt"
                href="https://www.youtube.com/channel/UC2u3tTpRpC2PYdZ7KF7rO0w"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Rizee on YouTube"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23 12s0-3.5-.4-5.1a2.7 2.7 0 0 0-1.9-1.9C19 4.5 12 4.5 12 4.5s-7 0-8.7.5A2.7 2.7 0 0 0 1.4 6.9C1 8.5 1 12 1 12s0 3.5.4 5.1a2.7 2.7 0 0 0 1.9 1.9c1.7.5 8.7.5 8.7.5s7 0 8.7-.5a2.7 2.7 0 0 0 1.9-1.9c.4-1.6.4-5.1.4-5.1ZM9.8 15.4V8.6l5.9 3.4-5.9 3.4Z" />
                </svg>
              </a>
              <a
                className="s-fb"
                href="https://www.facebook.com/Rizeeofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Rizee on Facebook"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.5 2.9h-2.3v7A10 10 0 0 0 22 12Z" />
                </svg>
              </a>
              <a
                className="s-ig"
                href="https://www.instagram.com/rizeeofficial/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Rizee on Instagram"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.2" cy="6.8" r="1.2" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a
                className="s-tg"
                href="https://t.me/rizeeofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Rizee on Telegram"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21.9 4.3 18.9 19c-.2 1-.8 1.3-1.7.8l-4.6-3.4-2.2 2.1c-.3.3-.5.5-1 .5l.3-4.7 8.5-7.7c.4-.3-.1-.5-.6-.2L7.1 13.1l-4.5-1.4c-1-.3-1-1 .2-1.5l17.6-6.8c.8-.3 1.5.2 1.5 1Z" />
                </svg>
              </a>
            </div>
          </footer>
        </div>

        {/* ==================== BACK TO TOP ==================== */}
        <button
          type="button"
          className={`totop ${this.state.showTop ? "on" : ""}`}
          onClick={this.scrollToTop}
          aria-label="Back to top"
          aria-hidden={!this.state.showTop}
          tabIndex={this.state.showTop ? 0 : -1}
        >
          <Stroke width={2.6}>
            <path d="M12 19V5" />
            <path d="m5 12 7-7 7 7" />
          </Stroke>
        </button>

        {/* ==================== LOGIN MODAL ==================== */}
        {this.renderLoginModal()}

        {/* ==================== REGISTER MODAL ==================== */}
        {this.renderModal()}
      </div>
    );
  }
}

export default withRouter(
  graphql(LOGIN_USER, { name: "login" })(
    graphql(FOUNDATION_GRADES, { name: "foundationGrades" })(
      graphql(FOUNDATION_LOGIN, { name: "foundationLogin" })(
        graphql(SEND_FOUNDATION_OTP, { name: "sendFoundationOtp" })(
          graphql(VERIFY_FOUNDATION_OTP, { name: "verifyFoundationOtp" })(
            graphql(REGISTER_FOUNDATION_STUDENT, { name: "registerFoundationStudent" })(
              IITFoundationLogin
            )
          )
        )
      )
    )
  )
);
